import { useSyncExternalStore } from 'react';
import { DEFAULT_LOOK, ITEMS, type LayerId, type Look } from '@/content/agentLook';

/**
 * The child's agent (docs/design/agent-builder.md): look and codename, on this device only (a game
 * key, so profiles and "Start over" include it). `made` says whether the child has built one yet,
 * which decides whether the builder opens after the intro.
 */
const KEY = 'foe-names.agent.v1';

interface Stored extends Look {
  made: boolean;
}

const listeners = new Set<() => void>();
let current: Stored = read();

function read(): Stored {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_LOOK, made: false };
    return clean(JSON.parse(raw) as Partial<Stored>);
  } catch {
    return { ...DEFAULT_LOOK, made: false };
  }
}

/** A stored look with unknown layers or items dropped (the item list may change between versions). */
export function clean(raw: Partial<Stored>): Stored {
  const items: Look['items'] = { ...DEFAULT_LOOK.items };
  for (const [layer, id] of Object.entries(raw.items ?? {}) as [LayerId, string][])
    if (layer in ITEMS && ITEMS[layer].some((i) => i.id === id)) items[layer] = id;
  return {
    items,
    colours: { ...DEFAULT_LOOK.colours, ...(raw.colours ?? {}) },
    codename: typeof raw.codename === 'string' ? raw.codename : null,
    made: raw.made === true,
  };
}

function write(next: Stored): void {
  current = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Storage blocked: the agent lasts until the page closes.
  }
  for (const l of listeners) l();
}

export const agentStore = {
  get: (): Stored => current,
  save: (look: Look): void => {
    write({ ...look, made: true });
  },
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

export function useAgent(): Stored {
  return useSyncExternalStore(agentStore.subscribe, agentStore.get);
}
